import React, { useRef, useState, useEffect } from "react";
import { Image, Modal } from "antd";
import { IoClose } from "react-icons/io5";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import classNames from "classnames";

// interfaces
import { ILocationSpaceCarousel } from "./ILocationSpaceCarousel";

// assets
import "swiper/css";
import "swiper/css/pagination";
import styles from "@/assets/styles/containers/location-space-carousel.module.scss";


function LocationSpaceCarousel(props: ILocationSpaceCarousel) {
  const {
    items,
    className,
    slidesPerView,
    imageStyle = {},
    children,
    loop = true,
    isSquareNavigation,
    isAutoplayByUseEffect,
    ...rest
  } = props;
  const swiperRef = useRef<SwiperCore | null>(null);

  const [modalVideo, setModalVideo] = useState<string | null>(null);

  const imagesToShow = items.length > 1 ? 1.3 : 1;

  const handlePrevClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    swiperRef.current?.slidePrev();
  }
  const handleNextClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    swiperRef.current?.slideNext();
  }

  // temporary solution for swiper autoplay at safari mobile & when several swiper carousels are on the same page
  useEffect(() => {
    if (swiperRef.current && isAutoplayByUseEffect) {
      setTimeout(() => {
        swiperRef.current?.autoplay?.stop();
        swiperRef.current?.autoplay?.start();
      }, 1000);
    }
  }, [swiperRef.current]);

  return (<>
    <Swiper
      slidesPerView={slidesPerView ? slidesPerView : imagesToShow}
      spaceBetween={24}
      modules={[Autoplay, Pagination]}
      pagination={{ clickable: true }}
      onBeforeInit={(swiper) => {
        swiperRef.current = swiper;
      }}
      loop={loop}
      className={classNames(
        styles["locl-location-space-carousel"],
        { [styles["locl-location-space-carousel__square-navigation"]]: isSquareNavigation },
        className
      )}
      data-testid="locl-location-space-carousel"
      {...rest}
    >
      {children}
      {items.length > 1 && (
        <div
          onClick={handlePrevClick}
          data-testid="locl-location-space-carousel-prev"
          className={`${styles["locl-location-space-carousel__navigation"]} ${styles["locl-location-space-carousel__navigation_left"]}`}
        >
          <FaChevronLeft />
        </div>
      )}
      {(items || []).map((item, index) => (
        <SwiperSlide
          key={`LocationSpaceCarousel-${index}`}
          className={styles["locl-location-space-carousel__slide"]}
        >
          <Image
            width="100%"
            height="100%"
            loading={index === 0 ? "eager" : "lazy"}
            src={item.src}
            alt={item.alt}
            className={styles["locl-location-space-carousel__slide-image"]}
            style={imageStyle}
            preview={false}
            onClick={() => setModalVideo(item.videoHtml as string)}
          />
        </SwiperSlide>
      ))}
      {items.length > 1 && (
        <div
          onClick={handleNextClick}
          className={`${styles["locl-location-space-carousel__navigation"]} ${styles["locl-location-space-carousel__navigation_right"]}`}
          data-testid="locl-location-space-carousel-next"
        >
          <FaChevronRight />
        </div>
      )}
    </Swiper>
    <Modal
      centered
      destroyOnClose
      footer={null}
      closeIcon={<IoClose />}
      className={styles["locl-video-modal"]}
      open={!!modalVideo}
      onCancel={() => setModalVideo(null)}
    >
      {modalVideo && (
        <div className={styles["locl-video-modal__video-wrapper"]}>
          <div className={styles["locl-video-modal__video-frame"]}
            dangerouslySetInnerHTML={{ __html: modalVideo }}
          />
        </div>
      )}
    </Modal>
  </>
  );
}

export default React.memo(LocationSpaceCarousel);
